import { Music, ThumbsUp, ExternalLink } from 'lucide-react';
import type { SongRequest } from '../../services/requests';
import { spotifyTrackIdFrom } from '../../utils/songIdentity';
import { formatDuration } from '../../utils/analyticsUtils';

interface Props {
  requests: SongRequest[];
}

export default function TopPerformers({ requests }: Props) {
  const sorted = [...requests].sort((a, b) => b.voteCount - a.voteCount).slice(0, 10);

  if (requests.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Top Requested Songs
        </h3>
        <div className="text-center py-8 text-gray-500">
          <Music className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No data available yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Top Requested Songs
      </h3>
      <div className="space-y-3">
        {sorted.map((r, index) => (
          <div
            key={r.id}
            className="flex items-center gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm">
              {index + 1}
            </div>
            {r.song.albumArtUrl && (
              <img
                src={r.song.albumArtUrl}
                alt=""
                className="w-10 h-10 rounded object-cover flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white truncate">
                {r.song.title}
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="truncate">{r.song.artist}</span>
                {r.song.durationMs && (
                  <span className="text-xs text-gray-400">({formatDuration(r.song.durationMs)})</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Hidden: Spotify API integration paused */}
              {false && r.spotifyUri && (
                <a
                  href={`https://open.spotify.com/track/${r.song.catalogIds?.spotify ?? spotifyTrackIdFrom(r.spotifyUri)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 hover:text-green-400"
                  title="Open in Spotify"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
              <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                <ThumbsUp className="w-4 h-4" />
                <span>{r.voteCount}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                r.status === 'played'
                  ? 'bg-success/10 text-success'
                  : r.status === 'queued' || r.status === 'nowPlaying'
                  ? 'bg-primary/10 text-primary'
                  : r.status === 'rejected'
                  ? 'bg-danger/10 text-danger'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              }`}>
                {r.status === 'nowPlaying' ? 'playing' : r.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
